<script lang="ts">
export default {
  inheritAttrs: false
};
</script>

<script setup lang="ts">
import LeTooltip from "@dsk/components/LeTooltip.vue";
import LeDoubleArrow from "@dsk/icons/LeDoubleArrow.vue";
import LeLongArrow from "@dsk/icons/LeLongArrow.vue";
import {
  Listbox as HListbox,
  ListboxButton as HListboxButton,
  ListboxOption as HListboxOption,
  ListboxOptions as HListboxOptions
} from "@headlessui/vue";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import type { SearchDirection } from "@/types";

const props = defineProps<{
  modelValue: SearchDirection;
  options: SearchDirection[];
}>();

const emit = defineEmits<{
  open: [];
  "update:modelValue": [direction: SearchDirection];
}>();

const { t } = useI18n();

const optionsRef = ref<typeof HListboxOptions>();
const selectedOption = computed<SearchDirection | undefined>({
  get() {
    return props.options.find((option) => option === props.modelValue);
  },
  set(option) {
    if (!option) {
      return;
    }

    emit("update:modelValue", option);
  }
});

const ariaLabel = computed<string>(() => {
  if (props.modelValue === "one-way") {
    return t("le-language-selector-direction-selector.aria.one-way");
  }

  return t("le-language-selector-direction-selector.aria.two-way");
});

const onOpen = () => {
  emit("open");
};
</script>

<template>
  <HListbox v-slot="{ disabled, open }" v-model="selectedOption" @keydown="$event.stopPropagation()">
    <div
      class="le-language-selector-direction-selector"
      :class="{
        'le-language-selector-direction-selector--disabled': disabled,
        'le-language-selector-direction-selector--open': open
      }"
    >
      <LeTooltip placement="top" :text="t('le-language-selector-direction-selector.tooltip')">
        <HListboxButton
          class="le-language-selector-direction-selector__button"
          :aria-label="ariaLabel"
          v-bind="$attrs"
          @keydown.enter="onOpen()"
          @keydown.space="onOpen()"
          @click="onOpen()"
        >
          <LeLongArrow v-if="selectedOption === 'one-way'" />
          <LeDoubleArrow v-else />
        </HListboxButton>
      </LeTooltip>

      <Transition name="dropdown">
        <HListboxOptions
          ref="optionsRef"
          class="le-language-selector-direction-selector__items"
          @keydown="$event.preventDefault()"
        >
          <HListboxOption
            v-for="option in options"
            v-slot="{ active, selected }"
            :key="option"
            :value="option"
            as="template"
          >
            <li
              class="le-language-selector-direction-selector__item"
              :class="{
                'le-language-selector-direction-selector__item--active': active,
                'le-language-selector-direction-selector__item--selected': selected
              }"
            >
              <template v-if="option === 'one-way'">
                <LeLongArrow />
                {{ t("le-language-selector-direction-selector.one-way") }}
              </template>
              <template v-else>
                <LeDoubleArrow />
                {{ t("le-language-selector-direction-selector.two-way") }}
              </template>
            </li>
          </HListboxOption>
        </HListboxOptions>
      </Transition>
    </div>
  </HListbox>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all";
@import "@/assets/styles/z-index.less";

.le-language-selector-direction-selector {
  display: inline-block;
  position: relative;
  .body-text("s");
  color: @colors[@neutrals][500] !important; // !important can be removed once we no longer have bootstrap styling globally

  &:focus-within {
    z-index: @le-language-selector-direction-selector__focus-within-z-index;
  }

  &__button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    padding: 10px;
    border: 1px solid @colors[@secondary][600];
    background: @colors[@secondary][100];

    &:hover {
      background: @colors[@secondary][300];
    }

    &::after {
      content: "";
      position: absolute;
      right: 0;
      bottom: 1px;
      width: 0;
      height: 0;
      border-width: 0 0 10px 10px;
      border-style: solid;
      border-color: transparent transparent @colors[@neutrals][500] transparent;
    }

    &.focus-visible {
      .focus-outline(green);
    }
  }

  &--disabled &__button {
    color: @colors[@secondary][600];
    cursor: default;
  }

  &--disabled &__button::after {
    opacity: 0.3;
  }

  &__items {
    position: absolute;
    z-index: @le-language-selector-direction-selector__items-z-index;
    top: 100%;
    right: 0;
    left: 0;
    width: fit-content;
    max-height: 60vh;
    margin-top: 4px;
    padding: 8px;
    overflow-y: auto;
    border: 1px solid @colors[@secondary][600];
    border-radius: 4px;
    outline: none;
    background: white;
    box-shadow: 0 1px 7px 0 rgba(0, 0, 0, 0.1);
  }

  &__item {
    display: grid;
    grid-auto-flow: column;
    align-items: center;
    justify-content: start;
    padding: 6px 10px;
    column-gap: 10px;
    border-radius: 4px;
    white-space: nowrap;
    cursor: pointer;

    &:not(:last-of-type) {
      margin-bottom: 6px;
    }

    &--selected,
    &--active {
      background-color: @colors[@secondary][200];
    }

    &--selected {
      border: 1px solid @colors[@secondary][600];
      font-weight: bold;
    }
  }
}
</style>
